import PropTypes from "prop-types";
import React, { useReducer } from "react";
import { chunk } from "lodash";

import API from "../../API";
import {
  POPULATE_OTA_VEHICLES,
  POPULATE_OTA_FILTER_PARAMS,
  POPULATE_SC_VERSIONS,
  POPULATE_BMS_VERSIONS,
  POPULATE_MD_VERSIONS,
  POPULATE_GEOFENCE_MAP_IDS,
  POPULATE_GEOFENCE_RULE_IDS,
  POPULATE_FIRMWARE_PACKAGES,
  POPULATE_GEOFENCE_PACKAGES,
  POPULATE_TARGET_GEOFENCE,
  POPULATE_TARGET_FIRMWARE,
  POPULATE_IN_PROGRESS_FPA_COUNT,
  POPULATE_IN_PROGRESS_GPA_COUNT,
  POPULATE_LG69T_GNSS_VERSIONS,
  POPULATE_LG69T_APP_VERSIONS,
} from "../types";
import OtaContext from "./otaContext";
import otaReducer from "./otaReducer";
import {
  fetchFirmwarePackages as _fetchFirmwarePackages,
  fetchGeofencePackages as _fetchGeofencePackages,
  binaryPackageReducer,
  combineReducers,
} from "../shared";
import { Parsers } from "../../util";

const OtaState = ({ children }) => {
  const MAX_FPA_PER_FLEET = Number(window.REACT_APP_MAX_FPA) || 0;
  const MAX_GPA_PER_FLEET = Number(window.REACT_APP_MAX_GPA) || 0;
  // Eventually we need to filter geofence packages via fleet field,
  // but currently that field isn't in filters and needs to be populated
  // on prod. Upping this page size is a hotfix for now.

  const otaOrderingOptions = [
    "id",
    "-id",
    "url",
    "-url",
    "shortcodes__shortcode",
    "-shortcodes__shortcode",
    "target_state",
    "-target_state",
    "last_reported_state",
    "-last_reported_state",
    "deployment_status",
    "-deployment_status",
    "battery__charge",
    "-battery__charge",
    "geofence_rules_id",
    "-geofence_rules_id",
    "geofence_map_id",
    "-geofence_map_id",
    "fleets",
    "-fleets",
    "created",
    "-created",
    "modified",
    "-modified",
    "possession_status",
    "-possession_status",
    "cell_strength",
    "-cell_strength",
    "maintenance",
    "-maintenance",
    "model_number",
    "-model_number",
    "serial_number",
    "-serial_number",
    "provisioning_status",
    "-provisioning_status",
    "last_ingest",
    "-last_ingest",
    "last_reported_network",
    "-last_reported_network",
    "radio_cert_expiration",
    "-radio_cert_expiration",
    "systemcontroller__firmware_version",
    "-systemcontroller__firmware_version",
    "motorcontroller__firmware_version",
    "-motorcontroller__firmware_version",
    "battery__firmware_version",
    "-battery__firmware_version",
    "systemcontroller__lg69t_gnss_version",
    "-systemcontroller__lg69t_gnss_version",
    "systemcontroller__lg69t_app_version",
    "-systemcontroller__lg69t_app_version",
    "firmware_package_application__in_progress",
    "-firmware_package_application__in_progress",
    "geofence_package_application__in_progress",
    "-geofence_package_application__in_progress",
    "scooter_type",
    "-scooter_type",
    "operational_speed_limit",
    "-operational_speed_limit",
  ];
  const initialOtaFilterParams = {
    fleets: null,
    page_size: Math.max(MAX_FPA_PER_FLEET, MAX_GPA_PER_FLEET),
    provisioning_status: undefined, // Should this always be 'provisioned'?
    ids__in: undefined,
    shortcodes__shortcode__in: undefined,
    model_number__icontains: undefined,
    serial_number__icontains: undefined,
    last_ingest__lte: undefined,
    last_ingest__gte: undefined,
    cell_strength__lte: undefined,
    cell_strength__gte: undefined,
    battery__charge__lte: undefined,
    battery__charge__gte: undefined,
    systemcontroller__firmware_version: undefined,
    motorcontroller__firmware_version: undefined,
    battery__firmware_version: undefined,
    systemcontroller__firmware_version__not: undefined,
    motorcontroller__firmware_version__not: undefined,
    battery__firmware_version__not: undefined,
    geofence_rules_id__not: undefined,
    geofence_map_id__not: undefined,
    target_state__in: [],
    last_reported_state__in: [],
    deployment_status__in: [],
    possession_status__in: [],
    maintenance: undefined,
    firmware_package_application__in_progress: undefined,
    geofence_package_application__in_progress: undefined,
    last_reported_network: undefined,
    radio_cert_expiration__lte: undefined,
    radio_cert_expiration__gte: undefined,
    systemcontroller__lg69t_app_version: undefined,
    systemcontroller__lg69t_gnss_version: undefined,
    scooter_type__in: [],
    ordering: [],
  };

  const packageTypes = ["Geofence", "Firmware"];

  const initialState = {
    inProgressFpaCount: null,
    inProgressGpaCount: null,
    otaVehicles: null,
    otaFilterParams: initialOtaFilterParams,
    scVersions: null,
    mdVersions: null,
    bmsVersions: null,
    geofenceRuleIds: null,
    geofenceMapIds: null,
    firmwarePackages: [],
    geofencePackages: [],
    firmwarePackageApplications: null,
    geofencePackageApplications: null,
    geofenceTargetPackage: null,
    firmwareTargetPackage: null,
    lg69tGnssVersions: null,
    lg69tAppVersions: null,
  };

  const [state, dispatch] = useReducer(
    combineReducers(otaReducer, binaryPackageReducer),
    initialState,
  );

  const setOtaFilterParams = (params = initialOtaFilterParams) => {
    dispatch({
      type: POPULATE_OTA_FILTER_PARAMS,
      payload: params ?? initialOtaFilterParams,
    });
  };

  const fetchInProgressOtaCount = async (fleetId) => {
    try {
      const [firmwareData, geofenceData] = await Promise.all([
        API.vehicle.listOtaVehicles({
          fleets: fleetId,
          firmware_package_application__in_progress: true,
        }),
        API.vehicle.listOtaVehicles({
          fleets: fleetId,
          geofence_package_application__in_progress: true,
        }),
      ]);
      const fpaPayload = {};
      const gpaPayload = {};
      fpaPayload[fleetId] = firmwareData?.count ?? 0;
      gpaPayload[fleetId] = geofenceData?.count ?? 0;
      dispatch({ type: POPULATE_IN_PROGRESS_FPA_COUNT, payload: fpaPayload });
      dispatch({ type: POPULATE_IN_PROGRESS_GPA_COUNT, payload: gpaPayload });
    } catch (err) {
      console.error(err, "fetchInProgressOtaCount");
    }
  };

  const fetchOtaVehicles = async (params, forCsv, page) => {
    const commaOrSpaces = /,\s+|,|\s+/;
    const parsedParams = {
      ...params,
      ...(params.ids__in ? { id__in: params.ids__in.split(commaOrSpaces).join() } : {}),
      ...(params.target_state__in && params.target_state__in.length
        ? { target_state__in: params.target_state__in.join() }
        : {}),
      ...(params.last_reported_state__in && params.last_reported_state__in.length
        ? { last_reported_state__in: params.last_reported_state__in.join() }
        : {}),
      ...(params.deployment_status__in && params.deployment_status__in.length
        ? { deployment_status__in: params.deployment_status__in.join() }
        : {}),
      ...(params.possession_status__in && params.possession_status__in.length
        ? { possession_status__in: params.possession_status__in.join() }
        : {}),
      ...(params.scooter_type__in && params.scooter_type__in.length
        ? { scooter_type__in: params.scooter_type__in.join() }
        : {}),
      ...(params.ordering && params.ordering.length ? { ordering: params.ordering.join() } : {}),
      ...(forCsv ? { page_size: 500 } : {}),
      ...(page ? { page } : {}),
    };
    try {
      if (parsedParams.fleets && !forCsv) {
        fetchInProgressOtaCount(parsedParams.fleets);
      }
      let data = {};
      let foundVehicles = [];
      if (params.shortcodes__shortcode__in) {
        const vehicleShortcodes = params.shortcodes__shortcode__in.split(commaOrSpaces);
        // eslint-disable-next-line no-restricted-syntax
        for (const shortcodesChunk of chunk(
          vehicleShortcodes,
          window.REACT_APP_MAX_VEHICLE_SHORTCODES || 50,
        )) {
          parsedParams.shortcodes__shortcode__in = shortcodesChunk.join();
          // eslint-disable-next-line no-await-in-loop
          const res = await API.vehicle.listOtaVehicles(parsedParams);
          foundVehicles = [...foundVehicles, ...res.results];
        }
        data.results = foundVehicles;
        data.count = foundVehicles.length;
      } else {
        data = await API.vehicle.listOtaVehicles(parsedParams);
      }
      const resultsNoDup = data.results.filter(
        (v, i, a) => a.findIndex((t) => t.id === v.id) === i,
      );
      const results = resultsNoDup.map((r) => ({
        ...r,
        created: Parsers.date(r.created),
        last_ingest: Parsers.date(r.last_ingest),
        modified: Parsers.date(r.modified),
      }));
      let current = 1;
      if (data.next) {
        const next = new URL(data.next).searchParams;
        current = parseInt(next.get("page"), 10) - 1;
      } else if (data.previous) {
        const previous = new URL(data.previous).searchParams;
        current = parseInt(previous.get("page") ?? 1, 10) + 1;
      }

      if (!forCsv) {
        dispatch({ type: POPULATE_OTA_VEHICLES, payload: { ...data, results, current } });
      } else {
        return { ...data, results, current };
      }
    } catch (err) {
      console.error(err, "fetchOtaVehicles");
    }
    return null;
  };

  const fetchFirmwareVersions = async () => {
    try {
      const params = { binary_type: "application" };
      // using three requests to overcome pagination limits
      const res = await Promise.all([
        API.firmware.list({ ...params, component_type: "SystemController" }),
        API.firmware.list({ ...params, component_type: "MotorController" }),
        API.firmware.list({ ...params, component_type: "Battery" }),
        API.firmware.list({ binary_type: "lg69t_gnss", component_type: "GNSS" }),
        API.firmware.list({ binary_type: "lg69t_app", component_type: "GNSS" }),
      ]);
      const versions = res.map((firmware) => [
        undefined,
        ...new Set(firmware.filter((f) => f.version).map((f) => f.version)),
      ]);
      dispatch({ type: POPULATE_SC_VERSIONS, payload: versions[0] });
      dispatch({ type: POPULATE_MD_VERSIONS, payload: versions[1] });
      dispatch({ type: POPULATE_BMS_VERSIONS, payload: versions[2] });
      dispatch({ type: POPULATE_LG69T_GNSS_VERSIONS, payload: versions[3] });
      dispatch({ type: POPULATE_LG69T_APP_VERSIONS, payload: versions[4] });
    } catch (err) {
      console.error(err, "fetchFirmwareVersions");
    }
  };

  const fetchFirmwarePackages = async () => {
    const fwp = await _fetchFirmwarePackages();
    dispatch({
      type: POPULATE_FIRMWARE_PACKAGES,
      payload: fwp,
    });
  };

  const fetchGeofencePackages = async (params) => {
    const [geoPacks, mapIds, ruleIds] = await _fetchGeofencePackages(params);
    dispatch({
      type: POPULATE_GEOFENCE_PACKAGES,
      payload: geoPacks,
    });
    dispatch({
      type: POPULATE_GEOFENCE_MAP_IDS,
      payload: mapIds,
    });
    dispatch({
      type: POPULATE_GEOFENCE_RULE_IDS,
      payload: ruleIds,
    });
  };

  const setGeofenceTargetPackage = (pkg) => {
    dispatch({ type: POPULATE_TARGET_GEOFENCE, payload: pkg });
  };

  const setFirmwareTargetPackage = (pkg) => {
    dispatch({ type: POPULATE_TARGET_FIRMWARE, payload: pkg });
  };

  const applyFirmwarePackage = async (vehicleIds, firmwarePackageId) => {
    try {
      await API.firmwarePackageApplications.bulkStart({
        vehicles: vehicleIds,
        firmware_package: firmwarePackageId,
      });
    } catch (err) {
      console.error(err, "applyFirmwarePackage");
    }
  };

  const stopFirmwarePackages = async (vehicleIds) => {
    try {
      await API.firmwarePackageApplications.bulkStop({ vehicles: vehicleIds });
    } catch (err) {
      console.error(err, "stopFirmwarePackages");
    }
  };

  const applyGeofencePackage = async (vehicleIds, geofencePackageId) => {
    try {
      await API.geofencePackageApplications.bulkStart({
        vehicles: vehicleIds,
        geofence_package: geofencePackageId,
      });
    } catch (err) {
      console.error(err, "applyGeofencePackage");
    }
  };

  const stopGeofencePackages = async (vehicleIds) => {
    try {
      await API.geofencePackageApplications.bulkStop({ vehicles: vehicleIds });
    } catch (err) {
      console.error(err, "stopGeofencePackages");
    }
  };

  const installPassiveHardware = async (vehicles) => {
    try {
      await API.vehicle.bulkUpdate(vehicles);
    } catch (err) {
      console.error(err, "installPassiveHardware");
    }
  };

  const fleetTransfer = async (vehicles) => {
    try {
      await API.vehicle.bulkUpdate(vehicles);
    } catch (err) {
      console.error(err, "fleetTransfer");
    }
  };

  const hibernateVehicles = async (fleetId, vehicles) => {
    try {
      await API.fleets.hibernateVehicles(fleetId, vehicles);
    } catch (err) {
      console.error(err, "hibernateVehicles");
    }
  };

  const hibernateVehiclesDetails = async (fleetId, vehicles) => {
    try {
      const details = await API.fleets.hibernateVehiclesDetails(fleetId, vehicles);
      return details;
    } catch (err) {
      console.error(err, "hibernateVehiclesDetails");
      return { error: err.message };
    }
  };

  return (
    <OtaContext.Provider
      value={{
        otaVehicles: state.otaVehicles,
        otaFilterParams: state.otaFilterParams,
        scVersions: state.scVersions,
        mdVersions: state.mdVersions,
        bmsVersions: state.bmsVersions,
        geofenceRuleIds: state.geofenceRuleIds,
        geofenceMapIds: state.geofenceMapIds,
        firmwarePackages: state.firmwarePackages,
        geofencePackages: state.geofencePackages,
        firmwarePackageApplications: state.firmwarePackageApplications,
        geofencePackageApplications: state.geofencePackageApplications,
        geofenceTargetPackage: state.geofenceTargetPackage,
        firmwareTargetPackage: state.firmwareTargetPackage,
        inProgressFpaCount: state.inProgressFpaCount,
        inProgressGpaCount: state.inProgressGpaCount,
        lg69tGnssVersions: state.lg69tGnssVersions,
        lg69tAppVersions: state.lg69tAppVersions,
        MAX_FPA_PER_FLEET,
        MAX_GPA_PER_FLEET,
        packageTypes,
        setGeofenceTargetPackage,
        setFirmwareTargetPackage,
        otaOrderingOptions,
        initialOtaFilterParams,
        setOtaFilterParams,
        fetchOtaVehicles,
        fetchFirmwareVersions,
        fetchFirmwarePackages,
        fetchGeofencePackages,
        applyGeofencePackage,
        applyFirmwarePackage,
        stopGeofencePackages,
        stopFirmwarePackages,
        fetchInProgressOtaCount,
        installPassiveHardware,
        fleetTransfer,
        hibernateVehicles,
        hibernateVehiclesDetails,
      }}
    >
      {children}
    </OtaContext.Provider>
  );
};

OtaState.propTypes = {
  children: PropTypes.node.isRequired,
};

export default OtaState;
